/*
 * lib/confetti.js
 *
 * Tiny canvas-based confetti viering — geen externe dependency. Spawnt
 * een fullscreen-canvas (pointer-events: none) bovenaan de z-index, blaast
 * ~150 deeltjes vanaf het midden naar boven met zwaartekracht, fadet uit
 * en ruimt zichzelf op na de animatie. Idempotent: meerdere keren aanroepen
 * stapelt extra bursts bovenop elkaar.
 *
 * Wordt geroepen vanuit actions.js surplusKies('spaar') — beloning voor
 * de "juiste keuze" zoals Lander voorstelde.
 */

const COLORS = ['#2a9d5a', '#e67e22', '#5c6bc0', '#e91e8c', '#f1c40f', '#16a1b5', '#8e44ad'];

export function celebrate({ particles = 150, duration = 2200 } = {}) {
  const canvas = document.createElement('canvas');
  canvas.style.cssText = `
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 99999;
  `;
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  let w = window.innerWidth;
  let h = window.innerHeight;

  const resize = () => {
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.width  = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width  = w + 'px';
    canvas.style.height = h + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener('resize', resize);

  // Spawn vanaf iets boven het midden — zodat ze visueel naar boven gaan
  // en daarna door zwaartekracht naar beneden vallen, alsof er een
  // raket-pop in de KPI-tegel afgaat.
  const originY = h * 0.55;
  const items = [];
  for (let i = 0; i < particles; i++) {
    const angle = (Math.random() - 0.5) * Math.PI * 0.7;   // -63° tot 63°
    const speed = 12 + Math.random() * 14;
    items.push({
      x: w / 2 + (Math.random() - 0.5) * 60,
      y: originY,
      vx: Math.sin(angle) * speed,
      vy: -Math.cos(angle) * speed,
      g: 0.45,
      drag: 0.985,
      size: 4 + Math.random() * 4,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rot: Math.random() * Math.PI * 2,
      rotVel: (Math.random() - 0.5) * 0.4,
      shape: Math.random() < 0.55 ? 'rect' : 'circle'
    });
  }

  const start = performance.now();

  function frame(now) {
    const elapsed = now - start;
    if (elapsed >= duration) {
      window.removeEventListener('resize', resize);
      canvas.remove();
      return;
    }

    ctx.clearRect(0, 0, w, h);

    // Lineair uitfaden in de laatste 35%.
    const fadeStart = duration * 0.65;
    const fade = elapsed > fadeStart
      ? Math.max(0, 1 - (elapsed - fadeStart) / (duration - fadeStart))
      : 1;

    items.forEach(p => {
      p.vy += p.g;
      p.vx *= p.drag;
      p.vy *= p.drag;
      p.x  += p.vx;
      p.y  += p.vy;
      p.rot += p.rotVel;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = fade;
      if (p.shape === 'rect') {
        ctx.fillRect(-p.size, -p.size / 2, p.size * 2, p.size);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    });

    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}
